import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FunctionComponent, SVGProps } from 'react';
import dayjs from 'dayjs';
import { Select, message } from 'antd';
import Pagination from '../../components/Pagination';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  NewEngagementIcon,
  SearchIcon,
  EditSquareIcon,
  TrashIcon,
  CheckmarkIcon,
  StatBoltIcon,
  StatCalendarIcon,
  StatCheckIcon,
  StatWarningIcon,
} from '../../assets/icons';
import {
  useGetDirectionsQuery,
  useGetDirectionStatsQuery,
  useDeleteDirectionMutation,
  dirCode,
  dirTitle,
  dirOneLine,
  dirDescription,
  dirDueDate,
  dirRawStatus,
  dirStatusKey,
  dirAssigneeNames,
  dirReferencePersonName,
  dirReferencePersonPhone,
  type DailyDirection,
  type UiDirStatus,
} from '../../features/dailyDirections/dailyDirectionApi';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;
/** Stat-card keys = the fields returned by GET /daily-direction/stats/. */
type StatKey = 'all' | 'to_do' | 'done' | 'closed' | 'doing';

const STATUS_META: Record<UiDirStatus, { label: string; pill: string; dot?: string; text: string; check?: boolean }> = {
  inprogress: { label: 'In Progress', pill: 'bg-[#f0fdf4]', dot: 'bg-[#22c55e]', text: 'text-[#15803d]' },
  pending: { label: 'Pending', pill: 'bg-[#f1f5f9]', dot: 'bg-[#94a3b8]', text: 'text-[#475569]' },
  assigned: { label: 'Assigned', pill: 'bg-[#f1f5f9]', dot: 'bg-[#0061ff]', text: 'text-[#475569]' },
  completed: { label: 'Completed', pill: 'bg-[#f0fdf4]', text: 'text-[#15803d]', check: true },
};

/** Colored pill per status (keyed by lowercased status value; `label` overrides display). */
const STATUS_STYLE: Record<string, { pill: string; text: string; dot?: string; check?: boolean; label?: string }> = {
  'to-do': { pill: 'bg-[#fbe8ff]', text: 'text-[#86198f]', dot: 'bg-[#c026d3]' },
  done: { pill: 'bg-[#dcfce7]', text: 'text-[#15803d]', check: true },
  doing: { pill: 'bg-[#fef3c7]', text: 'text-[#b45309]', dot: 'bg-[#f59e0b]' },
  assigned: { pill: 'bg-[#dbeafe]', text: 'text-[#1d4ed8]', dot: 'bg-[#3b82f6]' },
  'in progress': { pill: 'bg-[#fef3c7]', text: 'text-[#b45309]', dot: 'bg-[#f59e0b]' },
  completed: { pill: 'bg-[#dcfce7]', text: 'text-[#15803d]', check: true },
  closed: { pill: 'bg-[#e2e8f0]', text: 'text-[#334155]', dot: 'bg-[#64748b]' },
  discarded: { pill: 'bg-[#fee2e2]', text: 'text-[#b91c1c]', dot: 'bg-[#ef4444]', label: 'Disposed Off' },
  'disposed off': { pill: 'bg-[#fee2e2]', text: 'text-[#b91c1c]', dot: 'bg-[#ef4444]' },
  'list to be maintained': { pill: 'bg-[#6b21a8]', text: 'text-white', dot: 'bg-[#e9d5ff]' },
  listed: { pill: 'bg-[#dbeafe]', text: 'text-[#1d4ed8]', dot: 'bg-[#3b82f6]' },
  'listed in house': { pill: 'bg-[#57430e]', text: 'text-[#fef9c3]', dot: 'bg-[#eab308]' },
  discuss: { pill: 'bg-[#e2e8f0]', text: 'text-[#475569]', dot: 'bg-[#94a3b8]' },
};

/** Stat card → the `status` value sent to the server (undefined = no filter). */
const STATUS_PARAM: Record<StatKey, string | undefined> = {
  all: undefined,
  to_do: 'To-Do',
  done: 'Done',
  closed: 'Closed',
  doing: 'Doing',
};

interface StatCard {
  key: StatKey;
  value: string;
  label: string;
  Icon?: IconType;
  iconColor?: string;
  total?: boolean;
  overdue?: boolean;
}

/** Fallback figures shown until the stats endpoint responds. */
const STAT_CARDS: StatCard[] = [
  { key: 'all', value: '0', label: 'Total Command Load', total: true },
  { key: 'to_do', value: '0', label: 'To-Do', Icon: StatCalendarIcon, iconColor: 'text-[#c026d3]' },
  { key: 'done', value: '0', label: 'Done', Icon: StatCheckIcon, iconColor: 'text-[#16a34a]' },
  { key: 'closed', value: '0', label: 'Closed', Icon: StatCheckIcon, iconColor: 'text-[#64748b]' },
  { key: 'doing', value: '0', label: 'Doing', Icon: StatBoltIcon, iconColor: 'text-[#f59e0b]' },
];
/** Status filter dropdown options ("Disposed Off" is sent to the server as "Discarded"). */
const STATUS_OPTIONS = [
  { value: 'To-Do', label: 'To-Do' },
  { value: 'Done', label: 'Done' },
  { value: 'Closed', label: 'Closed' },
  { value: 'Doing', label: 'Doing' },
  { value: 'Discarded', label: 'Disposed Off' },
  { value: 'List to Be Maintained', label: 'List to Be Maintained' },
  { value: 'Listed', label: 'Listed' },
  { value: 'Listed In House', label: 'Listed In House' },
  { value: 'Discuss', label: 'Discuss' },
];

const GRID = 'grid grid-cols-[96px_minmax(0,1fr)_170px_150px_120px_150px_110px] items-center';
const TH = 'text-xs font-bold uppercase tracking-[0.6px] text-[#64748b]';
const AVATAR_TINTS = ['bg-[#dbeafe] text-[#0061ff]', 'bg-[#e0e7ff] text-[#4f46e5]', 'bg-[#f3e8ff] text-[#9333ea]'];

function StatusPill({ status }: { status: string }) {
  const raw = (status || '').trim();
  // Prefer an exact custom-status style; fall back to the normalized legacy buckets.
  const custom = STATUS_STYLE[raw.toLowerCase()];
  const m = custom
    ? { ...custom, label: custom.label ?? raw }
    : STATUS_META[dirStatusKey(raw)];
  return (
    <span className={['inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium', m.pill, m.text].join(' ')}>
      {m.check ? (
        <CheckmarkIcon className="size-2.5" />
      ) : (
        <span className={['size-1.5 rounded-full', m.dot ?? 'bg-current/50'].join(' ')} />
      )}
      {m.label}
    </span>
  );
}

/** "Today" / "Tomorrow" / "MMM D" from an ISO date. */
function scheduleLabel(iso: string): { text: string; today: boolean } {
  if (!iso) return { text: '—', today: false };
  const d = dayjs(iso);
  const today = d.isSame(dayjs(), 'day');
  if (today) return { text: 'Today', today: true };
  if (d.isSame(dayjs().add(1, 'day'), 'day')) return { text: 'Tomorrow', today: false };
  return { text: d.format('MMM D'), today: false };
}

export default function DailyDirectionsPage() {
  const navigate = useNavigate();
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StatKey>('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Debounce the search box; reset to page 1 on any query/filter change.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(queryInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [queryInput]);
  useEffect(() => setPage(1), [filter]);

  const { data, isFetching } = useGetDirectionsQuery({
    ...(query ? { search: query } : {}),
    // The dropdown's explicit status wins over the stat-card bucket.
    ...(statusFilter || STATUS_PARAM[filter] ? { status: statusFilter || STATUS_PARAM[filter] } : {}),
    page,
    pageSize,
  });
  const rows: DailyDirection[] = data?.items ?? [];
  const total = data?.total ?? 0;

  // Summary counts for the stat cards.
  const { data: stats } = useGetDirectionStatsQuery();
  const statValue = (c: StatCard): string | number => stats?.[c.key] ?? c.value;

  const [deleteDirection, { isLoading: isDeleting }] = useDeleteDirectionMutation();
  const [dirToDelete, setDirToDelete] = useState<DailyDirection | null>(null);
  const confirmDelete = async () => {
    if (!dirToDelete) return;
    try {
      await deleteDirection(dirToDelete.id).unwrap();
      message.success('Direction deleted.');
      setDirToDelete(null);
    } catch {
      message.error('Could not delete the direction. Please try again.');
    }
  };

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-[0.6px] text-brand">
            Strategic Operations
          </span>
          <h1 className="text-2xl font-semibold text-ink">Daily Directions</h1>
        </div>
        <button
          type="button"
          onClick={() => navigate('/user/directions/new')}
          className="flex items-center justify-center gap-2 self-start rounded-lg bg-brand px-5 py-2.5 text-base font-medium text-white shadow-btn transition-colors hover:bg-brand-dark sm:self-auto"
        >
          <NewEngagementIcon className="size-4" />
          New Direction
        </button>
      </div>

      {/* Stat cards — clickable filters; the selected card takes the blue treatment */}
      <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 xl:grid-cols-5">
        {STAT_CARDS.map((c) => {
          const selected = filter === c.key;
          const container = selected
            ? 'bg-brand text-white'
            : c.overdue
              ? 'border border-[#fee2e2] bg-[#fef2f2]'
              : 'border border-[#f1f5f9] bg-white hover:border-brand/40';

          if (c.total) {
            // The total card reflects the live server count for the current filter.
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setFilter(c.key)}
                aria-pressed={selected}
                className={['relative flex h-44 flex-col justify-between overflow-hidden rounded-xl p-6 text-left shadow-card transition', container].join(' ')}
              >
                {selected && <div className="absolute -bottom-6 -right-6 size-36 rounded-full bg-white/10" />}
                <span
                  className={['relative text-sm font-medium uppercase leading-5 tracking-[0.7px]', selected ? 'text-[#dbeafe]' : 'text-ink-secondary'].join(' ')}
                >
                  Total Command Load
                </span>
                <div className="relative flex items-baseline gap-2">
                  <span className={['text-5xl font-bold leading-none', selected ? 'text-white' : 'text-ink'].join(' ')}>
                    {stats?.all ?? total}
                  </span>
                  <span className={['text-base font-medium', selected ? 'text-[#dbeafe]' : 'text-ink-secondary'].join(' ')}>
                    Tasks
                  </span>
                </div>
              </button>
            );
          }

          const numColor = selected ? 'text-white' : c.overdue ? 'text-[#dc2626]' : 'text-ink';
          const labelColor = selected ? 'text-white/80' : c.overdue ? 'text-[#f87171]' : 'text-ink-secondary';
          const iconColor = selected ? 'text-white' : c.iconColor;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setFilter(c.key)}
              aria-pressed={selected}
              className={['flex h-44 flex-col justify-center gap-3 rounded-xl p-6 text-left shadow-card transition', container].join(' ')}
            >
              <span className="flex size-10 items-center justify-center">
                {c.Icon && <c.Icon className={['size-6', iconColor].join(' ')} />}
              </span>
              <span className={['text-4xl font-bold leading-10', numColor].join(' ')}>{statValue(c)}</span>
              <span className={['text-xs font-semibold uppercase tracking-[0.6px]', labelColor].join(' ')}>
                {c.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search + status filter */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative sm:w-1/2">
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#99a2b2]" />
          <input
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder="Global search for direction IDs, personnel, or locations..."
            className="w-full rounded-lg border border-transparent bg-white pl-11 pr-4 text-base text-ink placeholder:text-[#99a2b2] shadow-card focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
          />
        </div>
        <div className="sm:w-1/2">
          <Select
            className="h-11 w-full"
            placeholder="All Statuses"
            allowClear
            showSearch
            optionFilterProp="label"
            value={statusFilter || undefined}
            onChange={(v) => {
              setStatusFilter(v ?? '');
              setPage(1);
            }}
            options={STATUS_OPTIONS}
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#f1f5f9] bg-white shadow-card">
        <div className="overflow-x-auto">
          <div className="min-w-[1080px]">
            {/* Header */}
            <div className={[GRID, 'border-b border-[#f1f5f9] bg-[#f8fafc] px-6 py-4'].join(' ')}>
              <span className={TH}>ID</span>
              <span className={TH}>Direction</span>
              <span className={TH}>Assignee</span>
              <span className={TH}>Reference Person</span>
              <span className={TH}>Schedule</span>
              <span className={TH}>Status</span>
              <span className={[TH, 'text-right'].join(' ')}>Actions</span>
            </div>

            {/* Rows */}
            {isFetching && rows.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-ink-secondary">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-ink-secondary">No directions found.</div>
            ) : (
              rows.map((d, i) => {
                const assignees = dirAssigneeNames(d);
                const sched = scheduleLabel(dirDueDate(d));
                return (
                  <div key={d.id} className={[GRID, 'px-6 py-5', i > 0 ? 'border-t border-[#f1f5f9]' : ''].join(' ')}>
                    {/* ID */}
                    <span className="text-sm font-semibold text-brand">{dirCode(d)}</span>

                    {/* Direction */}
                    <div className="flex min-w-0 flex-col gap-0.5 pr-4">
                      <button
                        type="button"
                        onClick={() => navigate(`/user/directions/${d.id}`)}
                        className="text-left text-base font-semibold text-brand transition-colors hover:text-brand-dark"
                      >
                        {dirTitle(d)}
                      </button>
                      <span className="text-sm text-[#64748b]">{dirOneLine(d) || dirDescription(d)}</span>
                    </div>

                    {/* Assignee(s) */}
                    <div className="flex min-w-0 items-center gap-2">
                      {assignees.length ? (
                        <>
                          <div className="flex -space-x-2">
                            {assignees.slice(0, 3).map((name, idx) => (
                              <span
                                key={idx}
                                title={name}
                                className={[
                                  'flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-white text-xs font-bold',
                                  AVATAR_TINTS[(i + idx) % AVATAR_TINTS.length],
                                ].join(' ')}
                              >
                                {name.charAt(0).toUpperCase()}
                              </span>
                            ))}
                            {assignees.length > 3 && (
                              <span
                                title={assignees.slice(3).join(', ')}
                                className="flex size-8 shrink-0 items-center justify-center rounded-full border-2 border-white bg-[#e2e8f0] text-xs font-bold text-[#475569]"
                              >
                                +{assignees.length - 3}
                              </span>
                            )}
                          </div>
                          {assignees.length === 1 && (
                            <span className="truncate text-sm font-medium text-[#334155]">
                              {assignees[0]}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-sm text-ink-secondary">Unassigned</span>
                      )}
                    </div>

                    {/* Reference Person */}
                    <div className="flex min-w-0 flex-col pr-4">
                      <span className="truncate text-sm text-[#334155]">
                        {dirReferencePersonName(d) || '—'}
                      </span>
                      {dirReferencePersonPhone(d) && (
                        <span className="truncate text-xs text-ink-secondary">
                          {dirReferencePersonPhone(d)}
                        </span>
                      )}
                    </div>

                    {/* Schedule */}
                    <div>
                      <span
                        className={[
                          'inline-flex items-center rounded px-2.5 py-1 text-xs font-medium',
                          sched.today ? 'bg-[#eff6ff] text-brand' : 'bg-[#f1f5f9] text-[#475569]',
                        ].join(' ')}
                      >
                        {sched.text}
                      </span>
                    </div>

                    {/* Status */}
                    <div>
                      <StatusPill status={dirRawStatus(d)} />
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2 text-ink-secondary">
                      <button
                        type="button"
                        aria-label="Edit"
                        onClick={() => navigate(`/user/directions/${d.id}/edit`)}
                        className="transition-colors hover:text-brand"
                      >
                        <EditSquareIcon className="size-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Delete"
                        onClick={() => setDirToDelete(d)}
                        className="transition-colors hover:text-red-500"
                      >
                        <TrashIcon className="size-4" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}

            {/* Footer */}
            <Pagination
              rangeStart={total === 0 ? 0 : (page - 1) * pageSize + 1}
              rangeEnd={Math.min(page * pageSize, total)}
              total={total}
              page={page}
              totalPages={Math.max(1, Math.ceil(total / pageSize))}
              onChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(v) => {
                setPageSize(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        open={dirToDelete !== null}
        title="Delete Direction"
        message={
          dirToDelete ? (
            <>
              Are you sure you want to delete{' '}
              <span className="font-semibold text-ink">“{dirTitle(dirToDelete)}”</span>? This action
              cannot be undone.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Delete Direction"
        loading={isDeleting}
        onClose={() => setDirToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
